import { useState } from 'react';
import { Plus, Trash2, Mail, Send, Clock, Loader2, CheckCircle, XCircle, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { batchMailboxChanges, BatchPermissionChange } from '../../services/api';
import { useJobTracker } from '../../providers/JobTrackerProvider';

interface BatchMailboxEditorProps {
  clientId: string;
  userId: string;
  userEmail: string;
  userDisplayName: string;
  onComplete?: () => void;
  // Pre-populated cached data
  cachedForwarding?: string | null;
  cachedAutoReplyEnabled?: boolean;
  cachedDelegates?: { delegate: string; fullAccess: boolean; sendAs: boolean; sendOnBehalf: boolean }[];
}

export default function BatchMailboxEditor({
  clientId,
  userId,
  userEmail,
  userDisplayName,
  onComplete,
  cachedForwarding,
  cachedAutoReplyEnabled,
  cachedDelegates,
}: BatchMailboxEditorProps) {
  const { addJob, updateJob } = useJobTracker();

  // Permission changes to submit
  const [permChanges, setPermChanges] = useState<BatchPermissionChange[]>([]);
  const [newDelegateEmail, setNewDelegateEmail] = useState('');
  const [newPerms, setNewPerms] = useState({ fullAccess: true, sendAs: false, sendOnBehalf: false });

  // Forwarding
  const [changeForwarding, setChangeForwarding] = useState(false);
  const [fwdAction, setFwdAction] = useState<'set' | 'remove'>(cachedForwarding ? 'remove' : 'set');
  const [fwdAddress, setFwdAddress] = useState('');
  const [fwdKeepCopy, setFwdKeepCopy] = useState(true);

  // Out of Office
  const [changeOOO, setChangeOOO] = useState(false);
  const [oooAction, setOooAction] = useState<'enable' | 'disable'>(cachedAutoReplyEnabled ? 'disable' : 'enable');
  const [oooInternal, setOooInternal] = useState('');
  const [oooExternal, setOooExternal] = useState('');

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ success: boolean; error?: string } | null>(null);

  const addPermChange = () => {
    if (!newDelegateEmail.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    if (!newPerms.fullAccess && !newPerms.sendAs && !newPerms.sendOnBehalf) {
      toast.error('Select at least one permission');
      return;
    }
    setPermChanges(prev => [...prev, {
      action: 'add',
      delegateEmail: newDelegateEmail.trim(),
      ...newPerms,
    }]);
    setNewDelegateEmail('');
    setNewPerms({ fullAccess: true, sendAs: false, sendOnBehalf: false });
  };

  const addRemoveDelegate = (email: string) => {
    setPermChanges(prev => [...prev, { action: 'remove', delegateEmail: email }]);
  };

  const removePermChange = (index: number) => {
    setPermChanges(prev => prev.filter((_, i) => i !== index));
  };

  const totalChanges = permChanges.length + (changeForwarding ? 1 : 0) + (changeOOO ? 1 : 0);

  const handleSubmit = async () => {
    if (totalChanges === 0) {
      toast.error('No changes to submit');
      return;
    }

    setSubmitting(true);
    setResult(null);

    const jobId = `mailbox-batch-${userId}-${Date.now()}`;
    addJob({
      id: jobId,
      type: 'mailbox-batch',
      label: `Mailbox changes for ${userDisplayName}`,
    });

    try {
      const response = await batchMailboxChanges(clientId, userId, userEmail, {
        permissions: permChanges.length > 0 ? permChanges : undefined,
        forwarding: changeForwarding ? {
          action: fwdAction,
          forwardingAddress: fwdAction === 'set' ? fwdAddress : undefined,
          deliverToMailboxAndForward: fwdAction === 'set' ? fwdKeepCopy : undefined,
        } : undefined,
        outOfOffice: changeOOO ? {
          action: oooAction,
          internalMessage: oooAction === 'enable' ? oooInternal : undefined,
          externalMessage: oooAction === 'enable' ? oooExternal : undefined,
        } : undefined,
      });

      if (response.success) {
        setResult({ success: true });
        updateJob(jobId, { status: 'completed', completedAt: new Date().toISOString() });
        toast.success(`All ${totalChanges} mailbox changes applied`, { duration: 5000 });
        // Reset form
        setPermChanges([]);
        setChangeForwarding(false);
        setChangeOOO(false);
        onComplete?.();
      } else {
        setResult({ success: false, error: response.error });
        updateJob(jobId, { status: 'failed', completedAt: new Date().toISOString(), error: response.error });
        toast.error(response.error || 'Batch changes failed', { duration: 8000 });
      }
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Request failed';
      setResult({ success: false, error: errMsg });
      updateJob(jobId, { status: 'failed', completedAt: new Date().toISOString(), error: errMsg });
      toast.error(errMsg, { duration: 8000 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Section: Delegate Permissions */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
          <Mail className="h-4 w-4" />
          Delegate Permissions
        </h4>

        {/* Existing delegates from cache */}
        {cachedDelegates && cachedDelegates.length > 0 && (
          <div className="mb-3">
            <p className="text-xs text-muted-foreground mb-1">Current delegates:</p>
            <div className="space-y-1">
              {cachedDelegates.map((d, i) => {
                const isBeingRemoved = permChanges.some(pc => pc.action === 'remove' && pc.delegateEmail === d.delegate);
                return (
                  <div key={i} className={`flex items-center justify-between text-sm py-1.5 px-2 rounded ${isBeingRemoved ? 'bg-red-50 dark:bg-red-900/20 line-through opacity-60' : 'bg-muted'}`}>
                    <span className="text-foreground">{d.delegate}</span>
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        {d.fullAccess && <span className="text-[10px] bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1 py-0.5 rounded">Full</span>}
                        {d.sendAs && <span className="text-[10px] bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-1 py-0.5 rounded">SendAs</span>}
                        {d.sendOnBehalf && <span className="text-[10px] bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-1 py-0.5 rounded">OnBehalf</span>}
                      </div>
                      {!isBeingRemoved && (
                        <button onClick={() => addRemoveDelegate(d.delegate)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Pending changes */}
        {permChanges.filter(pc => pc.action === 'add').length > 0 && (
          <div className="mb-3">
            <p className="text-xs text-muted-foreground mb-1">Adding:</p>
            <div className="space-y-1">
              {permChanges.map((pc, i) => {
                if (pc.action !== 'add') return null;
                return (
                  <div key={i} className="flex items-center justify-between text-sm py-1.5 px-2 rounded bg-green-50 dark:bg-green-900/20">
                    <span className="text-foreground">{pc.delegateEmail}</span>
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        {pc.fullAccess && <span className="text-[10px] bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1 py-0.5 rounded">Full</span>}
                        {pc.sendAs && <span className="text-[10px] bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-1 py-0.5 rounded">SendAs</span>}
                        {pc.sendOnBehalf && <span className="text-[10px] bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-1 py-0.5 rounded">OnBehalf</span>}
                      </div>
                      <button onClick={() => removePermChange(i)} className="text-muted-foreground hover:text-foreground">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Add delegate form */}
        <div className="border border-dashed border-border rounded-lg p-3 space-y-2">
          <div className="flex gap-2">
            <input
              type="email"
              value={newDelegateEmail}
              onChange={e => setNewDelegateEmail(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addPermChange(); } }}
              placeholder="delegate@company.com"
              className="flex-1 px-2 py-1.5 text-sm border border-border rounded bg-card text-foreground"
            />
            <button
              onClick={addPermChange}
              disabled={!newDelegateEmail.includes('@')}
              className="px-2 py-1.5 text-sm rounded bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
          <div className="flex gap-3">
            <label className="flex items-center gap-1 text-xs text-muted-foreground">
              <input type="checkbox" checked={newPerms.fullAccess} onChange={e => setNewPerms({ ...newPerms, fullAccess: e.target.checked })} className="h-3 w-3" />
              Full Access
            </label>
            <label className="flex items-center gap-1 text-xs text-muted-foreground">
              <input type="checkbox" checked={newPerms.sendAs} onChange={e => setNewPerms({ ...newPerms, sendAs: e.target.checked })} className="h-3 w-3" />
              Send As
            </label>
            <label className="flex items-center gap-1 text-xs text-muted-foreground">
              <input type="checkbox" checked={newPerms.sendOnBehalf} onChange={e => setNewPerms({ ...newPerms, sendOnBehalf: e.target.checked })} className="h-3 w-3" />
              Send on Behalf
            </label>
          </div>
        </div>
      </div>

      {/* Section: Mail Forwarding */}
      <div className="bg-card border border-border rounded-lg p-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={changeForwarding}
            onChange={e => setChangeForwarding(e.target.checked)}
            className="h-4 w-4"
          />
          <Send className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">Change Mail Forwarding</span>
          {cachedForwarding && (
            <span className="text-xs text-amber-600">Currently: {cachedForwarding}</span>
          )}
        </label>

        {changeForwarding && (
          <div className="mt-3 ml-6 space-y-2">
            <div className="flex gap-4">
              <label className="flex items-center gap-1 text-sm">
                <input type="radio" name="fwdAction" value="set" checked={fwdAction === 'set'} onChange={() => setFwdAction('set')} />
                Set forwarding
              </label>
              <label className="flex items-center gap-1 text-sm">
                <input type="radio" name="fwdAction" value="remove" checked={fwdAction === 'remove'} onChange={() => setFwdAction('remove')} />
                Remove forwarding
              </label>
            </div>
            {fwdAction === 'set' && (
              <>
                <input
                  type="email"
                  value={fwdAddress}
                  onChange={e => setFwdAddress(e.target.value)}
                  placeholder="forward-to@company.com"
                  className="w-full px-2 py-1.5 text-sm border border-border rounded bg-card text-foreground"
                />
                <label className="flex items-center gap-1 text-xs text-muted-foreground">
                  <input type="checkbox" checked={fwdKeepCopy} onChange={e => setFwdKeepCopy(e.target.checked)} className="h-3 w-3" />
                  Keep a copy in mailbox
                </label>
              </>
            )}
          </div>
        )}
      </div>

      {/* Section: Out of Office */}
      <div className="bg-card border border-border rounded-lg p-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={changeOOO}
            onChange={e => setChangeOOO(e.target.checked)}
            className="h-4 w-4"
          />
          <Clock className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">Change Out of Office</span>
          {cachedAutoReplyEnabled && (
            <span className="text-xs text-amber-600">Currently enabled</span>
          )}
        </label>

        {changeOOO && (
          <div className="mt-3 ml-6 space-y-2">
            <div className="flex gap-4">
              <label className="flex items-center gap-1 text-sm">
                <input type="radio" name="oooAction" value="enable" checked={oooAction === 'enable'} onChange={() => setOooAction('enable')} />
                Enable
              </label>
              <label className="flex items-center gap-1 text-sm">
                <input type="radio" name="oooAction" value="disable" checked={oooAction === 'disable'} onChange={() => setOooAction('disable')} />
                Disable
              </label>
            </div>
            {oooAction === 'enable' && (
              <>
                <div>
                  <label className="text-xs text-muted-foreground">Internal message:</label>
                  <textarea
                    value={oooInternal}
                    onChange={e => setOooInternal(e.target.value)}
                    rows={2}
                    className="w-full px-2 py-1.5 text-sm border border-border rounded bg-card text-foreground mt-1"
                    placeholder="I am currently out of the office..."
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">External message:</label>
                  <textarea
                    value={oooExternal}
                    onChange={e => setOooExternal(e.target.value)}
                    rows={2}
                    className="w-full px-2 py-1.5 text-sm border border-border rounded bg-card text-foreground mt-1"
                    placeholder="Thank you for your email. I am currently out of the office..."
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Submit */}
      {totalChanges > 0 && (
        <div className="flex items-center justify-between bg-muted rounded-lg px-4 py-3">
          <span className="text-sm text-muted-foreground">
            {totalChanges} change{totalChanges > 1 ? 's' : ''} — single Exchange connection (~90s)
          </span>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Applying changes...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4" />
                Apply All Changes
              </>
            )}
          </button>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className={`rounded-lg p-3 ${result.success ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
          <div className="flex items-center gap-2">
            {result.success ? (
              <CheckCircle className="h-4 w-4 text-green-600" />
            ) : (
              <XCircle className="h-4 w-4 text-red-600" />
            )}
            <span className={`text-sm ${result.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
              {result.success ? 'All changes applied successfully' : result.error}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
